import React from 'react';
import { ExternalLink } from 'lucide-react';
import { PublicSection, Reveal } from '@/components/public';
import { POS_PLATFORMS } from '@/config/integrations';

export default function PosMatrixSection({ onViewDetails }) {
  return (
    <PublicSection
      id="pos-matrix"
      testId="pos-matrix-section"
      variant="default"
      eyebrow="Compare POS Options"
      title="Active POS & Aggregator Integrations"
      description="Five live integrations, side by side — pick the platform that fits your venue and volume."
      padTop="pt-16 md:pt-20"
      padBottom="pb-16 md:pb-20"
    >
      <Reveal>
        <div className="overflow-x-auto border border-border" data-testid="pos-matrix-table-wrap">
          <table className="w-full min-w-[860px] border-collapse">
            <thead>
              <tr className="bg-secondary">
                {['Platform', 'Target Venue', 'Software Cost', 'Sync Type', 'Status', 'Action'].map((h) => (
                  <th
                    key={h}
                    className="text-left p-4 text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground border-b border-border"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {POS_PLATFORMS.map((pos) => (
                <tr
                  key={pos.id}
                  data-testid={`pos-matrix-row-${pos.id}`}
                  className="border-b border-border last:border-0 hover:bg-surface-hover transition-colors"
                >
                  <td className="p-4 align-middle">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center justify-center w-20 h-8 shrink-0">
                        <img src={pos.logo} alt={pos.logoAlt} className="max-h-full max-w-full object-contain" loading="lazy" />
                      </span>
                      <span className="text-sm font-heading font-bold text-foreground whitespace-nowrap">{pos.name}</span>
                    </div>
                  </td>
                  <td className="p-4 align-middle text-sm text-muted-foreground">{pos.targetVenue}</td>
                  <td className="p-4 align-middle text-sm text-foreground font-medium whitespace-nowrap">{pos.cost}</td>
                  <td className="p-4 align-middle text-sm text-muted-foreground">{pos.syncType}</td>
                  <td className="p-4 align-middle">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-emerald-500/30 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 text-[11px] font-bold uppercase tracking-wide whitespace-nowrap">
                      <span aria-hidden className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                      {pos.status}
                    </span>
                  </td>
                  <td className="p-4 align-middle">
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => onViewDetails(pos.id)}
                        data-testid={`pos-matrix-view-details-${pos.id}`}
                        className="inline-flex items-center h-9 px-4 rounded-full border border-border bg-card text-xs font-semibold text-foreground hover:border-primary hover:text-primary transition-colors"
                      >
                        View Details
                      </button>
                      <a
                        href={pos.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`pos-matrix-explore-${pos.id}`}
                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary-hover transition-colors"
                      >
                        Explore POS <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Reveal>
      <Reveal delay={0.08}>
        <p className="mt-4 text-xs text-muted-foreground leading-relaxed">
          Software costs are set by each POS provider and may change.
        </p>
      </Reveal>
    </PublicSection>
  );
}
